// DJSEM Prompt 118 — Playlist Campaign Builder + Pitch Pack + Targets.
//
// Owns playlist campaigns, target planning, and pitch packs — pure logic. No scraping,
// no spam outreach, no automated pitching. Target list is a manual planner + Task Inbox integration.

/** The goal a playlist campaign is being run to achieve. */
export type DJSEMPlaylistCampaignGoal =
  | "release_awareness" | "fan_growth" | "gig_promotion"
  | "soundcloud_growth" | "spotify_growth" | "brand_positioning";

/** Lifecycle status of a playlist campaign. */
export type DJSEMPlaylistCampaignStatus = "draft" | "active" | "completed" | "archived";

/** A playlist promotion campaign: goals, targets, tasks, and linked assets. */
export interface DJSEMPlaylistCampaign {
  id: string;
  organisationId?: string;
  projectId?: string;
  releaseId?: string;
  playlistId: string;
  title: string;
  goals: DJSEMPlaylistCampaignGoal[];
  status: DJSEMPlaylistCampaignStatus;
  targetPlatform?: "spotify" | "soundcloud" | "multi";
  targetPlaylists: DJSEMPlaylistCampaignTarget[];
  taskIds: string[];
  assetIds: string[];
  publicPageId?: string;
  createdAt: string;
  updatedAt: string;
}

/** Outreach status of a single playlist campaign target. */
export type DJSEMPlaylistCampaignTargetStatus =
  | "research" | "to_contact" | "contacted" | "follow_up" | "accepted" | "rejected" | "not_applicable";

/** A single manually-planned playlist/curator target within a campaign. */
export interface DJSEMPlaylistCampaignTarget {
  id: string;
  label: string;
  platform?: "spotify" | "soundcloud" | "other";
  url?: string;
  contactName?: string;
  contactEmail?: string;
  status: DJSEMPlaylistCampaignTargetStatus;
  notes?: string;
}

/** A shareable pitch pack for a playlist campaign (description, bio, social copy, assets). */
export interface DJSEMPlaylistPitchPack {
  id: string;
  campaignId: string;
  title: string;
  playlistDescription: string;
  artistBio?: string;
  releaseSummary?: string;
  socialCopy: string[];
  assetIds: string[];
  shareLinkId?: string;
  warnings: string[];
  createdAt: string;
}

// ---- campaign lifecycle -------------------------------------------------------------------------

/** Input for {@link createPlaylistCampaign}. */
export interface CreatePlaylistCampaignInput {
  playlistId: string;
  title: string;
  goals: DJSEMPlaylistCampaignGoal[];
  releaseId?: string;
  targetPlatform?: "spotify" | "soundcloud" | "multi";
  organisationId?: string;
  projectId?: string;
}

/**
 * Create a new draft playlist campaign.
 * @param input - the campaign's playlist id, title, goals, and optional linkage fields.
 * @param now - the creation timestamp (defaults to now).
 * @returns the created draft campaign.
 */
export function createPlaylistCampaign(input: CreatePlaylistCampaignInput, now = new Date().toISOString()): DJSEMPlaylistCampaign {
  return {
    id: `plcamp_${Date.parse(now) || 0}`,
    organisationId: input.organisationId,
    projectId: input.projectId,
    releaseId: input.releaseId,
    playlistId: input.playlistId,
    title: input.title,
    goals: input.goals,
    status: "draft",
    targetPlatform: input.targetPlatform,
    targetPlaylists: [],
    taskIds: [],
    assetIds: [],
    createdAt: now,
    updatedAt: now,
  };
}

/** Deterministic template of follow-up tasks generated from the campaign's goals. */
export function generateCampaignTaskTemplates(goals: DJSEMPlaylistCampaignGoal[]): string[] {
  const t = new Set<string>();
  t.add("draft pitch pack");
  t.add("compile playlist target list");
  if (goals.includes("release_awareness")) { t.add("cross-post release announcement"); t.add("schedule teaser clips"); }
  if (goals.includes("spotify_growth")) t.add("submit to Spotify editorial via Spotify for Artists (manual)");
  if (goals.includes("soundcloud_growth")) t.add("reach out to compatible SoundCloud curators");
  if (goals.includes("gig_promotion")) t.add("attach playlist to upcoming gig announcement");
  if (goals.includes("brand_positioning")) t.add("brand consistency review of playlist artwork");
  if (goals.includes("fan_growth")) t.add("share pitch pack with fan CRM segment");
  return Array.from(t);
}

// ---- targets ------------------------------------------------------------------------------------

/**
 * Add a new target to a campaign's manually-planned target list.
 * @param campaign - the campaign to update.
 * @param target - the target's fields (id/status auto-assigned; status defaults to "research").
 * @param now - the update timestamp (defaults to now).
 * @returns the updated campaign.
 */
export function addCampaignTarget(campaign: DJSEMPlaylistCampaign, target: Omit<DJSEMPlaylistCampaignTarget, "id" | "status"> & Partial<Pick<DJSEMPlaylistCampaignTarget, "status">>, now = new Date().toISOString()): DJSEMPlaylistCampaign {
  const t: DJSEMPlaylistCampaignTarget = {
    id: `tgt_${campaign.targetPlaylists.length + 1}_${Date.parse(now) || 0}`,
    label: target.label,
    platform: target.platform,
    url: target.url,
    contactName: target.contactName,
    contactEmail: target.contactEmail,
    status: target.status ?? "research",
    notes: target.notes,
  };
  return { ...campaign, targetPlaylists: [...campaign.targetPlaylists, t], updatedAt: now };
}

/**
 * Update a single target's outreach status within a campaign.
 * @param campaign - the campaign to update.
 * @param targetId - the target's id.
 * @param status - the new status.
 * @param now - the update timestamp (defaults to now).
 * @returns the updated campaign (unchanged if the target id is not found).
 */
export function updateTargetStatus(campaign: DJSEMPlaylistCampaign, targetId: string, status: DJSEMPlaylistCampaignTargetStatus, now = new Date().toISOString()): DJSEMPlaylistCampaign {
  return {
    ...campaign,
    targetPlaylists: campaign.targetPlaylists.map((t) => (t.id === targetId ? { ...t, status } : t)),
    updatedAt: now,
  };
}

// ---- pitch pack ---------------------------------------------------------------------------------

/** Input for {@link createPitchPack}. */
export interface CreatePitchPackInput {
  campaignId: string;
  title: string;
  playlistDescription: string;
  artistBio?: string;
  releaseSummary?: string;
  socialCopy?: string[];
  assetIds?: string[];
  shareLinkId?: string;
}

/**
 * Build a pitch pack for a campaign, warning on commonly-missing recommended fields (bio, social copy, assets).
 * @param input - the campaign id, title/description, and optional bio/summary/copy/assets.
 * @param now - the creation timestamp (defaults to now).
 * @returns the constructed pitch pack.
 */
export function createPitchPack(input: CreatePitchPackInput, now = new Date().toISOString()): DJSEMPlaylistPitchPack {
  const warnings: string[] = [];
  if (!input.artistBio) warnings.push("artist bio missing — pitch packs are stronger with a short bio");
  if ((input.socialCopy ?? []).length === 0) warnings.push("no social copy variants — add 2–3 short variants for platform reuse");
  if ((input.assetIds ?? []).length === 0) warnings.push("no assets attached — add at least a playlist cover");
  return {
    id: `pitch_${input.campaignId}_${Date.parse(now) || 0}`,
    campaignId: input.campaignId,
    title: input.title,
    playlistDescription: input.playlistDescription,
    artistBio: input.artistBio,
    releaseSummary: input.releaseSummary,
    socialCopy: input.socialCopy ?? [],
    assetIds: input.assetIds ?? [],
    shareLinkId: input.shareLinkId,
    warnings,
    createdAt: now,
  };
}
